import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonSelect,
  IonSelectOption,
  IonText,
} from '@ionic/react';
import { arrowBack, documentTextOutline } from 'ionicons/icons';
import type { GridApi } from 'ag-grid-community';
import { reportingService } from '@/services/ReportingService';
import ModalFooter from '@/components/common/ModalFooter';

type Separator = 'comma' | 'tab' | 'pipe';

interface Props {
  isOpen: boolean;
  gridApi: GridApi | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const SEPARATOR_MAP: Record<Separator, string> = {
  comma: ',',
  tab: '\t',
  pipe: '|',
};

const ExportCsvModal: React.FC<Props> = ({ isOpen, gridApi, onClose, onSuccess }) => {
  const [filename, setFilename] = useState('report-export');
  const [separator, setSeparator] = useState<Separator>('comma');
  const [error, setError] = useState<string | null>(null);

  const handleExport = () => {
    const validationError = reportingService.validateFilename(filename.trim());
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!gridApi) return;

    gridApi.exportDataAsCsv({
      fileName: `${filename.trim()}.csv`,
      columnSeparator: SEPARATOR_MAP[separator],
      onlySelected: true,
    });

    onSuccess(`CSV exported as "${filename.trim()}.csv"`);
    onClose();
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose} style={{ '--width': '480px', '--height': '65vh', '--max-height': '90vh' }}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Export to CSV</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ '--padding-top': '0', '--padding-bottom': '0' }}>
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', minHeight: '160px' }}>

          {/* File name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#444' }}>
              File Name
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="text"
                value={filename}
                onChange={(e) => { setFilename(e.target.value); setError(null); }}
                placeholder="report-export"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  fontSize: '0.95rem',
                  border: error ? '1px solid var(--ion-color-danger)' : '1px solid #ccc',
                  borderRadius: '6px',
                  outline: 'none',
                  background: '#fff',
                  color: '#333',
                }}
              />
              <span style={{ fontSize: '0.9rem', color: '#666', whiteSpace: 'nowrap' }}>.csv</span>
            </div>
            {error && (
              <IonText color="danger" style={{ fontSize: '0.8rem' }}>{error}</IonText>
            )}
          </div>

          {/* Separator */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#444' }}>
              Column Separator
            </label>
            <IonSelect
              value={separator}
              onIonChange={(e) => setSeparator(e.detail.value)}
              interface="popover"
              style={{
                border: '1px solid #ccc',
                borderRadius: '6px',
                padding: '4px 8px',
                background: '#fff',
              }}
            >
              <IonSelectOption value="comma">Comma ( , )</IonSelectOption>
              <IonSelectOption value="tab">Tab ( ⇥ )</IonSelectOption>
              <IonSelectOption value="pipe">Pipe ( | )</IonSelectOption>
            </IonSelect>
          </div>

          <IonText color="medium" style={{ fontSize: '0.78rem' }}>
            Only selected rows will be exported. Filename may contain letters, numbers, spaces, hyphens, underscores, and periods.
          </IonText>
        </div>
      </IonContent>

      <ModalFooter
        cancelLabel="Cancel"
        actionLabel="Export CSV"
        cancelIcon={arrowBack}
        actionIcon={documentTextOutline}
        onCancel={handleClose}
        onAction={handleExport}
        cancelFill="outline"
      />
    </IonModal>
  );
};

export default ExportCsvModal;
